import classes from './ReleasePage.module.css';
import AccordionSideNav from '../../layout/SideNav/AccordionSideNav/AccordionSideNav';
import AuthContext from '../../../store/auth-context';
import React, { useContext, useRef, useLayoutEffect, useState, useMemo } from 'react';
import { getFilteredPageDataList } from '../../../util/filteredPageData';
import LoadingPage from '../../../components/UI/Loading';
import useValidatePageAccess from '../../../hooks/useValidatePageAccess';
import { PageDataContext } from '../../../App';

const ReleasePage = () => {
  const authCtx = useContext(AuthContext);
  const pageDataList = useContext(PageDataContext);
  const yearRefs = useRef({});
  const solSer = authCtx.userInfo?.sol_ser ?? '';
  const affNo = Number(authCtx.userInfo?.aff_no || 0);

  useValidatePageAccess(authCtx.isLoggedIn && authCtx.userInfo?.m_no);

  const releaseData = useMemo(() => {
    return pageDataList.find(p => p.url === '/release')?.data || [];
  }, [pageDataList]);

  // 그룹/아이템/visibleApis 권한 전처리 함수
  function preprocessReleaseData(data, solSer, affNo) {
    if (!Array.isArray(data)) return [];
    const isRestricted = api => api.authorized_sol_ser || api.authorized_aff_no?.length;
    const isVisible = api =>
      (!api.authorized_sol_ser || solSer.includes(api.authorized_sol_ser)) &&
      (!api.authorized_aff_no?.length || api.authorized_aff_no.includes(affNo));
    let prevYear = null;
    return data
      .map(group => {
        const year = group.date.slice(0, 4);
        const showYearHeader = year !== prevYear;
        prevYear = year;
        const items = (group.items || [])
          .map(item => {
            const apis = item.apis || [];
            const total = apis.length;
            const authCount = apis.filter(isRestricted).length;
            const visibleApis = apis.filter(isVisible);
            if (total > 0 && authCount === total) {
              const allVisible = visibleApis.length > 0;
              return {
                ...item,
                visibleApis,
                _hideByAuth: !allVisible
              };
            }
            return {
              ...item,
              visibleApis,
              _hideByAuth: false
            };
          })
          .filter(item => !item._hideByAuth && (item.visibleApis.length > 0 || item.summary));
        return {
          ...group,
          year,
          showYearHeader,
          items
        };
      })
      .filter(group => group.items.length > 0);
  }

  const releaseDataWithYearHeader = useMemo(() => preprocessReleaseData(releaseData, solSer, affNo), [releaseData, solSer, affNo]);

  const years = useMemo(() => {
    return [...new Set(releaseDataWithYearHeader.map(v => v.year))];
  }, [releaseDataWithYearHeader]);

  const [activeYear, setActiveYear] = useState('');

  // ref setter
  const setYearRef = (year) => (el) => {
    if (el) yearRefs.current[year] = el;
  };

  useLayoutEffect(() => {
    const updateActiveYear = () => {
      const NAV_OFFSET = 160;
      let currentYear = years[0];

      for (const year of years) {
        const section = yearRefs.current[year];
        if (!section) continue;

        const rect = section.getBoundingClientRect();

        if (rect.top - NAV_OFFSET <= 0) {
          currentYear = year;
        } else {
          break;
        }
      }

      setActiveYear(currentYear);
    };

    // scroll throttle
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;

      requestAnimationFrame(() => {
        updateActiveYear();
        ticking = false;
      });
    };

    updateActiveYear();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };

  }, [years]);

  if (authCtx.isLoggedIn && !authCtx.userInfo.m_no) {
    return <LoadingPage message="사용자 정보를 불러오는 중..." />;
  }

  const filteredPageDataList = getFilteredPageDataList(authCtx, pageDataList);

  return (
    <>
      <AccordionSideNav data={filteredPageDataList} />
      <div className={classes.container}>
        <h1 className={classes.heading}>API 변경내역</h1>
        {releaseDataWithYearHeader.length === 0 ? (
          <div>변경 내역이 없습니다.</div>
        ) : (
          releaseDataWithYearHeader.map((group) => {
            return (
              <div key={group.date}>
                {group.showYearHeader && (
                  <h2
                    id={`year-${group.year}`}
                    className={classes.year}
                    ref={setYearRef(group.year)}
                  >
                    {group.year}
                  </h2>
                )}
                <div className={classes.section}>
                  <h3 className={classes.date}>{group.date}</h3>
                  {group.items.map((item, i) => (
                    <div key={i} className={classes.typeBlock}>
                      <div className={`${classes.type} ${classes[item.type]}`}>
                        {item.type}
                      </div>
                      {item.summary && (
                        <div className={classes.summary}>{item.summary}</div>
                      )}
                      {item.visibleApis.map((api, j) => (
                        <div key={j} className={classes.item}>
                          <div className={classes.endpointRow}>
                            {api.method && (
                              <span className={`${classes.method} ${classes[api.method]}`}>
                                {api.method}
                              </span>
                            )}
                            {api.action && (
                              <span className={classes.endpoint}>
                                https://openapi.playauto.io/api{api.action}
                              </span>
                            )}
                          </div>
                          {api.detail && (
                            <div className={classes.detail}>{api.detail}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className={classes.yearNav}>
        {years.map(year => (
          <a
            key={year}
            href={`#year-${year}`}
            className={`${classes.yearLink} ${activeYear === year ? classes.activeYear : ''}`}
          >
            {year}
          </a>
        ))}
      </div>
    </>
  );
};

export default ReleasePage;